import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Images, Loader2, Play, Upload, Download, Eye, Star } from "lucide-react";

const ORIENT_LABEL = { vertical: "Vertical", square: "Square", landscape: "Landscape" };
const PERIODS = [{ v: "7d", l: "7 days" }, { v: "30d", l: "30 days" }, { v: "90d", l: "90 days" }, { v: "all", l: "All time" }];
const fmtDate = (v) => (v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const EMPTY_FORM = { title: "", description: "", color_codes: [], formats: [], applications: [], orientation: "", tags: "", featured: false };

function ChipPicker({ label, options, values, onChange, testid }) {
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={testid}>
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => toggle(o.value)} data-testid={`${testid}-${o.value}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              values.includes(o.value) ? "bg-charcoal text-white" : "border border-border bg-white text-warmgrey hover:border-charcoal"}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MetadataForm({ form, setForm, options, idPrefix }) {
  const colorOptions = (options?.colors || []).map((c) => ({ value: c.code, label: c.label }));
  const addColor = (code) => {
    if (code && code !== "NONE" && !form.color_codes.includes(code)) setForm((f) => ({ ...f, color_codes: [...f.color_codes, code] }));
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Title</label>
        <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="mt-1 h-11 bg-white focus-visible:ring-terracotta" data-testid={`${idPrefix}-title`} />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Description <span className="normal-case text-muted-foreground">(optional)</span></label>
        <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          rows={2} className="mt-1 bg-white focus-visible:ring-terracotta" data-testid={`${idPrefix}-description`} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Products / colors</p>
        <Select value="NONE" onValueChange={addColor}>
          <SelectTrigger className="mt-1.5 h-10 bg-white" data-testid={`${idPrefix}-color-select`}>
            <SelectValue placeholder="Associate catalog colors…" />
          </SelectTrigger>
          <SelectContent className="max-h-72 bg-white">
            <SelectItem value="NONE">Associate catalog colors…</SelectItem>
            {colorOptions.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.color_codes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${idPrefix}-color-chips`}>
            {form.color_codes.map((code) => {
              const opt = colorOptions.find((c) => c.value === code);
              return (
                <button key={code} type="button"
                  onClick={() => setForm((f) => ({ ...f, color_codes: f.color_codes.filter((c) => c !== code) }))}
                  className="badge-status badge-neutral hover:border-status-sold" title="Remove">
                  {opt?.label || code} ×
                </button>
              );
            })}
          </div>
        )}
      </div>
      <ChipPicker label="Formats" testid={`${idPrefix}-formats`} values={form.formats}
        onChange={(v) => setForm((f) => ({ ...f, formats: v }))}
        options={(options?.formats || []).map((f) => ({ value: f, label: options.format_labels[f] || f }))} />
      <ChipPicker label="Applications" testid={`${idPrefix}-applications`} values={form.applications}
        onChange={(v) => setForm((f) => ({ ...f, applications: v }))}
        options={(options?.applications || []).map((a) => ({ value: a, label: a }))} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Orientation</label>
          <Select value={form.orientation || "NONE"} onValueChange={(v) => setForm((f) => ({ ...f, orientation: v === "NONE" ? "" : v }))}>
            <SelectTrigger className="mt-1 h-10 bg-white" data-testid={`${idPrefix}-orientation`}>
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="NONE">Not set</SelectItem>
              {(options?.orientations || []).map((o) => (
                <SelectItem key={o} value={o}>{ORIENT_LABEL[o]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Tags <span className="normal-case text-muted-foreground">(comma-separated)</span></label>
          <Input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            className="mt-1 h-10 bg-white focus-visible:ring-terracotta" data-testid={`${idPrefix}-tags`} placeholder="handmade, zellige, moroccan" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-charcoal">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
          data-testid={`${idPrefix}-featured`} className="h-4 w-4 accent-terracotta" />
        Featured asset
      </label>
    </div>
  );
}

function AnalyticsTab() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [recentPage, setRecentPage] = useState(1);
  const [sortKey, setSortKey] = useState("downloads");

  const load = useCallback((p = period, rp = 1) => {
    api.get("/admin/media/analytics", { params: { period: p, recent_page: rp } }).then((r) => setData(r.data));
    setRecentPage(rp);
  }, [period]);
  useEffect(() => { load(period, 1); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  const k = data.kpis;
  const sortedAssets = [...data.top_assets].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));

  const KpiCard = ({ label, value, testid }) => (
    <Card className="border-border bg-white" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{label}</p>
        <p className="mt-1 font-display text-3xl font-medium text-charcoal num">{value}</p>
      </CardContent>
    </Card>
  );

  const AggTable = ({ title, rows, labelKey = "key", testid }) => (
    <Card className="border-border bg-white" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{title}</p>
        {rows.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No engagement in this period.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-warmgrey">
                <th className="pb-2 font-medium">{title.split(" ")[0] === "Media" ? "Value" : title.replace(/^By |^Top /, "")}</th>
                <th className="pb-2 text-right font-medium">Views</th>
                <th className="pb-2 text-right font-medium">Downloads</th>
                <th className="pb-2 text-right font-medium">Dealers</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r[labelKey]} className="border-b border-limestone/60 last:border-0">
                  <td className="py-2 text-charcoal">{r[labelKey]}</td>
                  <td className="py-2 text-right num text-warmgrey">{r.views}</td>
                  <td className="py-2 text-right num text-warmgrey">{r.downloads}</td>
                  <td className="py-2 text-right num text-warmgrey">{r.unique_dealers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="media-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2" data-testid="media-analytics-periods">
          {PERIODS.map((p) => (
            <button key={p.v} type="button" onClick={() => setPeriod(p.v)} data-testid={`media-period-${p.v}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                period === p.v ? "bg-charcoal text-white" : "border border-border bg-white text-warmgrey hover:border-charcoal"}`}>
              {p.l}
            </button>
          ))}
        </div>
        <a href={`/api/admin/media/analytics/export.csv?period=${period}`} data-testid="media-export-csv"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-terracotta px-4 text-xs font-semibold text-terracotta transition-colors hover:bg-[#BC6955]/5">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </a>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <KpiCard label="Media views" value={k.views} testid="kpi-views" />
        <KpiCard label="Downloads" value={k.downloads} testid="kpi-downloads" />
        <KpiCard label="Dealers engaged" value={k.unique_dealers} testid="kpi-dealers" />
        <KpiCard label="Users engaged" value={k.unique_users} testid="kpi-users" />
        <KpiCard label="Download rate" value={`${k.download_rate}%`} testid="kpi-rate" />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Download rate = downloads / detail views. Media engagement is an interest signal — not sales demand or purchase intent.
      </p>

      <Card className="mt-5 border-border bg-white" data-testid="media-top-assets">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Top media</p>
            <div className="flex gap-1.5 text-[11px]">
              {[["downloads", "Downloads"], ["views", "Views"], ["download_rate", "Rate"]].map(([key, label]) => (
                <button key={key} type="button" onClick={() => setSortKey(key)} data-testid={`media-sort-${key}`}
                  className={`rounded-full px-2.5 py-0.5 ${sortKey === key ? "bg-charcoal text-white" : "text-warmgrey"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {sortedAssets.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No engagement yet.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-warmgrey">
                    <th className="pb-2 font-medium">Asset</th>
                    <th className="pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium">Colors</th>
                    <th className="pb-2 text-right font-medium">Views</th>
                    <th className="pb-2 text-right font-medium">Downloads</th>
                    <th className="pb-2 text-right font-medium">Dealers</th>
                    <th className="pb-2 text-right font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((r) => (
                    <tr key={r.asset_id} className="border-b border-limestone/60 last:border-0" data-testid={`media-top-${r.asset_id}`}>
                      <td className="py-2 text-charcoal">
                        {r.title}
                        {r.status !== "active" && <span className="ml-1.5 badge-status badge-neutral">{r.status}</span>}
                      </td>
                      <td className="py-2 capitalize text-warmgrey">{r.media_type}</td>
                      <td className="py-2 text-xs text-warmgrey">{(r.color_codes || []).join(", ") || "—"}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.views}</td>
                      <td className="py-2 text-right num text-charcoal">{r.downloads}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.unique_dealers}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.download_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <AggTable title="Product / color interest" rows={data.by_color} testid="media-by-color" />
        <AggTable title="Application interest" rows={data.by_application} testid="media-by-application" />
        <AggTable title="Media type" rows={data.by_media_type.map((r) => ({ ...r, key: r.key === "photo" ? "Photo" : r.key === "video" ? "Video" : r.key }))} testid="media-by-type" />
        <AggTable title="Orientation" rows={data.by_orientation.map((r) => ({ ...r, key: ORIENT_LABEL[r.key] || r.key }))} testid="media-by-orientation" />
      </div>

      <Card className="mt-5 border-border bg-white" data-testid="media-dealer-engagement">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Dealer engagement (admin only)</p>
          {data.dealer_engagement.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No dealer engagement in this period.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-warmgrey">
                    <th className="pb-2 font-medium">Dealer</th>
                    <th className="pb-2 text-right font-medium">Views</th>
                    <th className="pb-2 text-right font-medium">Downloads</th>
                    <th className="pb-2 text-right font-medium">Unique assets downloaded</th>
                    <th className="pb-2 text-right font-medium">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dealer_engagement.map((r) => (
                    <tr key={r.dealer_id} className="border-b border-limestone/60 last:border-0">
                      <td className="py-2 text-charcoal">{r.dealer_name}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.views}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.downloads}</td>
                      <td className="py-2 text-right num text-warmgrey">{r.unique_assets_downloaded}</td>
                      <td className="py-2 text-right text-xs text-warmgrey">{fmtDate(r.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-5 border-border bg-white" data-testid="media-recent-activity">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Recent activity</p>
          {data.recent.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No media activity yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {data.recent.map((e, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="text-xs text-warmgrey num">{fmtDate(e.at)}</span>
                  <span className="font-medium text-charcoal">{e.dealer_name}</span>
                  <span className="text-warmgrey">
                    {e.type === "MEDIA_DOWNLOAD" ? "downloaded" : "viewed"}
                  </span>
                  <span className="text-charcoal">{e.asset_title}</span>
                  {e.user_name && <span className="text-xs text-muted-foreground">({e.user_name}{e.location_label ? ` · ${e.location_label}` : ""})</span>}
                </li>
              ))}
            </ul>
          )}
          {data.recent_has_more && (
            <Button variant="outline" size="sm" className="mt-3 border-terracotta text-terracotta hover:bg-[#BC6955]/5"
              onClick={() => load(period, recentPage + 1)} data-testid="media-recent-load-more">
              Load more
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminMedia() {
  const [tab, setTab] = useState("library");
  const [assets, setAssets] = useState(null);
  const [options, setOptions] = useState(null);
  const [editAsset, setEditAsset] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/media").then((r) => setAssets(r.data)).catch(() => setAssets([]));
    if (!options) api.get("/admin/media/options").then((r) => setOptions(r.data)).catch(() => {});
  }, [options]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (a) => {
    setForm({
      title: a.title, description: a.description || "", color_codes: a.color_codes || [],
      formats: a.formats || [], applications: a.applications || [], orientation: a.orientation || "",
      tags: (a.tags || []).join(", "), featured: !!a.featured,
    });
    setReplaceFile(null);
    setEditAsset(a);
  };

  const metaPayload = () => ({
    title: form.title, description: form.description, color_codes: form.color_codes,
    formats: form.formats, applications: form.applications,
    orientation: form.orientation || null,
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    featured: form.featured,
  });

  const createAsset = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo or video file"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("meta", JSON.stringify(metaPayload()));
      await api.post("/admin/media", fd);
      toast.success("Media added to the library");
      setForm(EMPTY_FORM); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTab("library");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/media/${editAsset.id}`, metaPayload());
      if (replaceFile) {
        const fd = new FormData();
        fd.append("file", replaceFile);
        await api.post(`/admin/media/${editAsset.id}/file`, fd);
      }
      toast.success("Media updated");
      setEditAsset(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/media/${editAsset.id}/status`, { active: editAsset.status !== "active" });
      toast.success(editAsset.status === "active" ? "Media deactivated — hidden from dealers, analytics preserved" : "Media activated");
      setEditAsset(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-center gap-3">
        <Images className="h-5 w-5 text-terracotta" />
        <h1 className="font-display text-2xl font-medium text-charcoal">Media Library</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Approved Zelligio photography and video for showroom partners — manage content and understand dealer engagement.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white" data-testid="media-admin-tabs">
          <TabsTrigger value="library" data-testid="media-tab-library">Library</TabsTrigger>
          <TabsTrigger value="upload" data-testid="media-tab-upload">Upload / Add Media</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="media-tab-analytics">Media Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          {assets === null ? (
            <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>
          ) : assets.length === 0 ? (
            <p className="mt-8 text-sm text-muted-foreground" data-testid="media-library-empty">
              No media yet — use Upload / Add Media to start the library.
            </p>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-admin-grid">
              {assets.map((a) => (
                <button key={a.id} type="button" onClick={() => openEdit(a)} data-testid={`media-admin-card-${a.id}`}
                  className="group text-left">
                  <div className="relative aspect-square overflow-hidden rounded-lg border border-border bg-sand">
                    {a.media_type === "photo" ? (
                      <img src={`/api/admin/media/${a.id}/${a.has_thumb ? "thumb" : "file"}`} alt={a.title} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-charcoal/90">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-terracotta text-white">
                          <Play className="h-5 w-5 fill-current" />
                        </span>
                      </div>
                    )}
                    <div className="absolute left-2 top-2 flex gap-1.5">
                      <span className={`badge-status ${a.status === "active" ? "badge-available" : "badge-neutral"}`}>{a.status}</span>
                      {a.featured && <span className="badge-status badge-reserved"><Star className="mr-0.5 h-2.5 w-2.5" />Featured</span>}
                    </div>
                  </div>
                  <p className="mt-2 truncate text-sm font-medium text-charcoal">{a.title}</p>
                  <p className="text-xs text-warmgrey num">
                    <Eye className="mr-1 inline h-3 w-3" />{a.stats?.views ?? 0} views · {a.stats?.downloads ?? 0} downloads
                  </p>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="upload">
          <Card className="mt-5 max-w-2xl border-border bg-white" data-testid="media-upload-form">
            <CardContent className="p-6">
              <form onSubmit={createAsset} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Media file</label>
                  <Input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="media-file-input"
                    className="mt-1 h-11 bg-white file:mr-3 file:rounded-sm file:border-0 file:bg-charcoal file:px-3 file:py-1 file:text-xs file:text-white" />
                  <p className="mt-1 text-[11px] text-muted-foreground">Photos JPG/PNG/WebP up to 15MB · Videos MP4/MOV/WebM up to 100MB</p>
                </div>
                <MetadataForm form={form} setForm={setForm} options={options} idPrefix="media-new" />
                <Button type="submit" disabled={saving} data-testid="media-upload-submit"
                  className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Add to Media Library
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>

      <Dialog open={!!editAsset} onOpenChange={(o) => !o && setEditAsset(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-3xl" data-testid="media-edit-dialog">
          {editAsset && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-medium text-charcoal">{editAsset.title}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  {editAsset.media_type === "video" ? (
                    <video controls preload="metadata" src={`/api/admin/media/${editAsset.id}/file`}
                      className="max-h-[300px] w-full rounded-lg bg-charcoal" data-testid="media-edit-preview" />
                  ) : (
                    <img src={`/api/admin/media/${editAsset.id}/file`} alt={editAsset.title}
                      className="max-h-[300px] w-full rounded-lg bg-sand object-contain" data-testid="media-edit-preview" />
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {editAsset.original_filename} · {(editAsset.size / 1024 / 1024).toFixed(2)} MB · {editAsset.media_type}
                    {editAsset.width ? ` · ${editAsset.width}×${editAsset.height}` : ""}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground num" data-testid="media-edit-stats">
                    {editAsset.stats?.views ?? 0} views · {editAsset.stats?.downloads ?? 0} downloads (all time)
                  </p>
                  <div className="mt-3">
                    <label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Replace media file</label>
                    <Input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                      onChange={(e) => setReplaceFile(e.target.files?.[0] || null)} data-testid="media-replace-input"
                      className="mt-1 h-10 bg-white file:mr-3 file:rounded-sm file:border-0 file:bg-charcoal file:px-3 file:py-1 file:text-xs file:text-white" />
                    <p className="mt-1 text-[11px] text-muted-foreground">Replacement preserves the asset and all historical analytics.</p>
                  </div>
                  <Button variant="outline" onClick={toggleStatus} disabled={saving} data-testid="media-toggle-status"
                    className={`mt-3 h-10 w-full ${editAsset.status === "active" ? "border-status-sold/50 text-status-sold" : "border-terracotta text-terracotta"}`}>
                    {editAsset.status === "active" ? "Deactivate (hide from dealers)" : "Activate"}
                  </Button>
                </div>
                <div>
                  <MetadataForm form={form} setForm={setForm} options={options} idPrefix="media-edit" />
                  <Button onClick={saveEdit} disabled={saving} data-testid="media-edit-save"
                    className="mt-4 h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
